/* eslint-disable react/display-name */
import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Button, Bubble } from 'tea-component';
import { throttle } from 'lodash-es';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import useInput from './useInput';
import { storage } from '@/utils/utils';
import { DB_DASHBOARD_LOCAL_FILTER_SAVE_OUT_SCHEME_KEY, DB_DASHBOARD_LOCAL_FILTER_SAVE_SCHEME_KEY } from '@/pages/dashboard/_config/constant';
import { getCustomFilterList } from '../customFilterList';
import { DATE_RANGE_ARR } from './_config';
import { TIME_LIMIT_EXCLUDED_GAMES, RANGE_TIME_LIMIT, RANGE_TIME_LIMIT_BY_GAME, RANGE_TIME_LIMIT_NOT_GLOBAL } from '@/pages/dashboard/utils/_config/constant';
import { isRangeTimeOverLimit } from '@/pages/dashboard/utils/utils';
import { history } from 'umi';
import { IS_GLOBAL } from '@home/_config/config';

export default (props: any) => {
  const {
    inputSlot,// 筛选器功能插槽：现用于筛选器方案保存
    topSlot, // 头部插槽
    tailSlot, // 按钮后插槽
    bottomSlot, // 尾部插槽
    inputList = [], // 筛选器列表
    showButton = true, // 是否显示操作按钮
    filterSaveConfig, // 筛选器保存专用配置
    className = '',
    confirmType,
    resetType,
    latestDate,
    defaultValue = {},
    value,
    gameCode,
    onChange,
    onConfirm,
    onReset,
    showBubble,
    setShowBubble,
    extraSlot
  } = props;
  const { isHaveFilterSave, isOutScheme, selectorSchemeList, setSelectedValue, setFilterModalInfo } = filterSaveConfig || {};
  const { getLocalText, locale } = useLocal('filter');
  const curValue = value || defaultValue || {};
  const customFilters = getCustomFilterList();

  // 内部使用的value，无实际作用，只是用来触发组件更新
  const [internalValue, setInternalValue] = useState<any>(curValue);
  // 实际内部使用的value值
  const valueRef = useRef<any>(curValue);
  const [isReseted, setIsReseted] = useState<boolean>(false); // 是否重置过
  const { timeLimit } = history.location.query || {};
  const isNotLimitTime = useMemo(
    () =>
      timeLimit === 'false' ||
      TIME_LIMIT_EXCLUDED_GAMES.includes(gameCode),
    [timeLimit, gameCode],
  );

  // 判断是否超过时间范围限制
  // 使用 internalValue 而不是 curValue，因为 internalValue 会在选择时间时立即更新
  const overRangeTimeLimit = useMemo(() => {
    const currentValue = internalValue || defaultValue || {};
    if (!currentValue) return false;
    if (isNotLimitTime) return false;

    // 配对时间范围筛选器
    const matchedKey = DATE_RANGE_ARR.find((key) => key in currentValue);

    if (matchedKey) {
      let value = currentValue[matchedKey];

      // 多粒度筛选器需使用value字段
      if (!Array.isArray(value) && currentValue[matchedKey]?.value) {
        value = currentValue[matchedKey].value;
      }

      // 根据 gameCode 获取对应的时间限制，如果没有配置则使用默认值
      const timeLimit = IS_GLOBAL ? (RANGE_TIME_LIMIT_BY_GAME[gameCode] || RANGE_TIME_LIMIT) : RANGE_TIME_LIMIT_NOT_GLOBAL;

      return (Array.isArray(value) && value[0] && value[1])
        ? isRangeTimeOverLimit(value[0], value[1], 'years', timeLimit)
        : false;
    }

    return false;
  }, [internalValue, defaultValue, gameCode, isNotLimitTime]);
  const handleClickComfirm = useCallback(throttle(() => {
    if (confirmType) {
      return;
    }
    setShowBubble?.(false);
    // todo inputList 变化了，valueRef不一定有新的input项的值，defaultValue有默认值，旧平台inputList变化后，也是需要点击confirm触发
    onConfirm?.({ ...defaultValue, ...valueRef.current });
    setIsReseted(false);
  }, 500, { leading: true }), [showButton, onConfirm, confirmType]);

  const handleClickReset = useCallback(throttle(() => {
    if (!resetType) {
      const newValue = { ...defaultValue };
      valueRef.current = newValue;
      // 同步更新 internalValue，确保 overRangeTimeLimit 能正确计算
      setInternalValue(newValue);
      onReset?.(newValue);
      setIsReseted(true);
      // 重置筛选器方案下拉框，同时删除筛选器方案的缓存
      setSelectedValue?.({});

      storage.removeItem(isOutScheme ?
        DB_DASHBOARD_LOCAL_FILTER_SAVE_OUT_SCHEME_KEY : DB_DASHBOARD_LOCAL_FILTER_SAVE_SCHEME_KEY);
    }
  }, 500, { leading: true }), [showButton, onReset, resetType, defaultValue, isOutScheme, setSelectedValue]);


  const onFilterChange = useCallback((key: string, val: any, vewType: string) => {
    let value = val;
    // 加customFilters判断是因为两点：
    // 第一点：当前有些页面single筛选器传参是string不是数组，如竞品的来源筛选器
    // 第二点: 不能影响benchmark逻辑
    if (customFilters.includes(key) && vewType === 'single') {
      value = [val];
    }
    const newValue = {
      ...valueRef.current,
      [key]: value
    };
    onChange?.(key, value, newValue);
    // 更新value
    valueRef.current = newValue;
    // 触发组件更新
    setInternalValue(newValue);
    // 如果没有操作按钮，直接触发confirm
    if (!showButton) {
      onConfirm?.({ ...valueRef.current });
    }
  }, [onConfirm, showButton, onChange]);

  // 更新外部的value值到内部
  useMemo(() => {
    if (value) {
      valueRef.current = value;
    }
  }, [value]);

  useEffect(() => {
    // 同步更新 internalValue，确保 overRangeTimeLimit 能正确计算
    setInternalValue(value);
  }, [value]);

  const { renderInput } = useInput({
    gameCode,
    value,
    defaultValue,
    internalValue,
    inputList,
    latestDate,
    curValue: valueRef.current,
    locale,
    isReseted,
    getLocalText,
    onFilterChange,
    overRangeTimeLimit,
  });

  // const [visible, setVisible] = useState(false);
  return <div className={`mod-filter ${className}`}>
    {topSlot}
    <div className='mod-filter__inner dbd-filter-render-box'>
      {inputSlot}
      {renderInput}
      {extraSlot}
    </div>
    {/* {extraSlot} */}
    {
      <div className='margin-t-10 mod-filter-button'>
        <Bubble
          visible={showBubble}
          arrowPointAtCenter
          dark
          placement="top"
          style={{ maxWidth: 450 }}
          content={getLocalText(`lock_expend_message`)}
        >
          <Button
            type="primary"
            className='margin-r filter-tea-btn'
            onClick={handleClickComfirm}
            loading={confirmType === 'loading'}
            disabled={confirmType === 'disabled' || overRangeTimeLimit}
          >
            {
              getLocalText('confirm')
            }
          </Button>
        </Bubble>
        <Button
          type="weak"
          className='margin-r filter-tea-btn'
          onClick={handleClickReset}
          loading={resetType === 'loading'}
          disabled={resetType === 'disabled'}
        >
          {
            getLocalText('reset')
          }
        </Button>
        {
          isHaveFilterSave &&
          <Bubble
            dark
            content={selectorSchemeList?.length >= 10 ? getLocalText('save_up_to_10') : ''}>
            <Button type="weak" className='tea-btn--weak'
              onClick={() => {
                setFilterModalInfo({
                  filterModalType: 'add',
                  filterModalVisible: true,
                });
              }}
              disabled={selectorSchemeList?.length >= 10}>
              {getLocalText('save_setting')}
            </Button>
          </Bubble>
        }
        {tailSlot}
      </div>
    }
    {bottomSlot}
  </div>;
};
